import React, { useState, useEffect } from 'react';
import { Settings, Database, Zap, Image, Globe, Save, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAdminTheme, getThemeClasses } from '../contexts/AdminTheme';
import AdminThemeSwitcher from './AdminThemeSwitcher';

const AdminSettings = () => {
  const { theme } = useAdminTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [settings, setSettings] = useState({
    ai: {
      provider: 'ollama',
      ollamaUrl: 'http://localhost:11434',
      ollamaApiKey: '',
      model: 'gemma2',
      groqApiKey: '',
      groqModel: 'qwen/qwen3-32b',
      enabled: true,
      think: true,
      // Groq reasoning parameters
      reasoningFormat: 'parsed',
      reasoningEffort: 'default'
    },
    database: {
      mongoUrl: 'mongodb://localhost:27017/webc_v5',
      connectionStatus: 'unknown'
    },
    integrations: {
      imageProvider: 'unsplash', // Active image provider: 'unsplash' or 'pexels'
      unsplash: {
        accessKey: '',
        enabled: false,
        quality: 'regular',
        orientation: 'landscape'
      },
      pexels: {
        apiKey: '',
        enabled: true,
        quality: 'large',
        orientation: 'landscape'
      },
      openai: {
        apiKey: '',
        model: 'gpt-3.5-turbo',
        enabled: false
      }
    },
    system: {
      baseDomain: 'localhost:3000'
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Loaded settings:', data); // Debug log
        setSettings(data);
      } else {
        console.error('Failed to load settings:', response.status, response.statusText);
        if (response.status === 403) {
          console.error('Admin access denied');
        }
        const errorText = await response.text();
        console.error('Error response:', errorText);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const testAIConnection = async () => {
    setTestingConnection({ ai: true });
    
    // Show loading toast
    const loadingToast = toast.loading('Testing AI connection... This may take up to 30 seconds for model initialization.');
    
    try {
      const response = await fetch('/api/admin/test-ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings.ai)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`AI connection successful! Model: ${data.model}`, { id: loadingToast });
      } else {
        toast.error(`AI connection failed: ${data.message || 'Unknown error'}`, { id: loadingToast });
      }
    } catch (error) {
      console.error('AI test error:', error);
      toast.error('AI connection test failed: Network error', { id: loadingToast });
    } finally {
      setTestingConnection({});
    }
  };

  const testUnsplashConnection = async () => {
    setTestingConnection({ unsplash: true });
    
    const loadingToast = toast.loading('Testing Unsplash connection...');
    
    try {
      const response = await fetch('/api/admin/test-unsplash', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings.integrations.unsplash)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Unsplash connection successful! Found ${data.imageCount} images`, { id: loadingToast });
      } else {
        toast.error(`Unsplash connection failed: ${data.message || 'Unknown error'}`, { id: loadingToast });
      }
    } catch (error) {
      console.error('Unsplash test error:', error);
      toast.error('Unsplash connection test failed: Network error', { id: loadingToast });
    } finally {
      setTestingConnection({});
    }
  };

  const testPexelsConnection = async () => {
    setTestingConnection({ pexels: true });
    
    const loadingToast = toast.loading('Testing Pexels connection...');
    
    try {
      const response = await fetch('/api/admin/test-pexels', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(settings.integrations.pexels)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`Pexels connection successful! Found ${data.imageCount} images`, { id: loadingToast });
      } else {
        toast.error(`Pexels connection failed: ${data.message || 'Unknown error'}`, { id: loadingToast });
      }
    } catch (error) {
      console.error('Pexels test error:', error);
      toast.error('Pexels connection test failed: Network error', { id: loadingToast });
    } finally {
      setTestingConnection({});
    }
  };

  const updateSettings = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateIntegrationSettings = (integration, field, value) => {
    if (integration === 'imageProvider') {
      // Handle imageProvider as a direct property
      setSettings(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          imageProvider: value
        }
      }));
    } else {
      // Handle nested integration settings
      setSettings(prev => ({
        ...prev,
        integrations: {
          ...prev.integrations,
          [integration]: {
            ...prev.integrations[integration],
            [field]: value
          }
        }
      }));
    }
  };

  return (
    <div className={themeClasses.page}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Settings className={`w-8 h-8 ${theme.colors.textMuted}`} />
              <h1 className={`text-3xl ${themeClasses.heading}`}>Admin Settings</h1>
            </div>
            <p className={themeClasses.text}>Configure AI models, integrations, and system settings</p>
          </div>
          <AdminThemeSwitcher />
        </div>

        <div className="space-y-8">
          {/* AI Configuration */}
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Zap className={`w-6 h-6 ${themeClasses.textMuted}`} />
                <h2 className={`text-xl font-semibold ${themeClasses.heading}`}>AI Configuration</h2>
              </div>
              <button
                onClick={testAIConnection}
                disabled={testingConnection.ai}
                className={`${themeClasses.button} disabled:opacity-50`}
              >
                {testingConnection.ai ? 'Testing...' : 'Test Connection'}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.label}`}>
                  AI Provider
                </label>
                <select
                  value={settings.ai.provider}
                  onChange={(e) => updateSettings('ai', 'provider', e.target.value)}
                  className={`w-full ${themeClasses.input}`}
                >
                  <option value="ollama">Ollama (Local)</option>
                  <option value="groq">Groq (Cloud)</option>
                  <option value="openai">OpenAI</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.label}`}>
                  {settings.ai.provider === 'groq' ? 'Groq Model' : 'Model'}
                </label>
                <input
                  type="text"
                  value={settings.ai.provider === 'groq' ? settings.ai.groqModel : settings.ai.model}
                  onChange={(e) => updateSettings('ai', settings.ai.provider === 'groq' ? 'groqModel' : 'model', e.target.value)}
                  className={`w-full ${themeClasses.input}`}
                  placeholder={settings.ai.provider === 'groq' ? 'qwen/qwen3-32b' : 'gemma2'}
                />
              </div>

              {settings.ai.provider === 'ollama' && (
                <>
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium ${themeClasses.label}`}>
                      Base URL
                    </label>
                    <input
                      type="text"
                      value={settings.ai.ollamaUrl}
                      onChange={(e) => updateSettings('ai', 'ollamaUrl', e.target.value)}
                      className={`w-full ${themeClasses.input}`}
                      placeholder="http://localhost:11434"
                    />
                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                      Base URL for Ollama server or OpenAI-compatible API endpoint
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium ${themeClasses.label}`}>
                      API Key (Optional)
                    </label>
                    <input
                      type="password"
                      value={settings.ai.ollamaApiKey}
                      onChange={(e) => updateSettings('ai', 'ollamaApiKey', e.target.value)}
                      className={`w-full ${themeClasses.input}`}
                      placeholder="Enter API key for OpenAI-compatible APIs"
                    />
                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                      Required for most OpenAI-compatible APIs. Leave empty for local Ollama.
                    </p>
                  </div>
                </>
              )}

              {settings.ai.provider === 'groq' && (
                <>
                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium ${themeClasses.label}`}>
                      Groq API Key
                    </label>
                    <input
                      type="password"
                      value={settings.ai.groqApiKey}
                      onChange={(e) => updateSettings('ai', 'groqApiKey', e.target.value)}
                      className={`w-full ${themeClasses.input}`}
                      placeholder="gsk_..."
                    />
                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                      Get your Groq API key from{' '}
                      <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className={`${themeClasses.textMuted} hover:underline`}>
                        Groq Console
                      </a>
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.label}`}>
                      Reasoning Format
                    </label>
                    <select
                      value={settings.ai.reasoningFormat}
                      onChange={(e) => updateSettings('ai', 'reasoningFormat', e.target.value)}
                      className={`w-full ${themeClasses.input}`}
                    >
                      <option value="parsed">Parsed - Separate reasoning field</option>
                      <option value="raw">Raw - Reasoning in think tags</option>
                      <option value="hidden">Hidden - Final answer only</option>
                    </select>
                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                      Controls how reasoning is formatted in responses from reasoning models
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.label}`}>
                      Reasoning Effort
                    </label>
                    <select
                      value={settings.ai.reasoningEffort}
                      onChange={(e) => updateSettings('ai', 'reasoningEffort', e.target.value)}
                      className={`w-full ${themeClasses.input}`}
                    >
                      <option value="default">Default - Enable reasoning</option>
                      <option value="none">None - Disable reasoning</option>
                    </select>
                    <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                      Controls reasoning effort level (only supported by Qwen 3 32B)
                    </p>
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.ai.enabled}
                    onChange={(e) => updateSettings('ai', 'enabled', e.target.checked)}
                    className={`rounded ${themeClasses.input}`}
                  />
                  <span className={`text-sm font-medium ${themeClasses.text}`}>Enable AI content generation</span>
                </label>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings.ai.think}
                    onChange={(e) => updateSettings('ai', 'think', e.target.checked)}
                    className={`rounded ${themeClasses.input}`}
                  />
                  <span className={`text-sm font-medium ${themeClasses.text}`}>Enable AI thinking/reasoning mode</span>
                </label>
                <p className={`text-xs ${themeClasses.textMuted} mt-1 ml-6`}>
                  When enabled, AI models will use step-by-step reasoning for higher quality content generation
                </p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-200">Connection Status</h3>
                  {settings.ai.provider === 'ollama' && (
                    <>
                      <p className="text-sm text-yellow-300 mt-1">
                        For Ollama: Make sure it's running with <code className="bg-yellow-100 px-1 rounded">ollama serve</code>
                      </p>
                      <p className="text-sm text-yellow-300 mt-1">
                        For OpenAI-compatible APIs: Use the base URL (e.g., http://localhost:8080/v1)
                      </p>
                      <p className="text-sm text-yellow-300 mt-1">
                        First connection may take up to 30 seconds for model initialization.
                      </p>
                    </>
                  )}
                  {settings.ai.provider === 'groq' && (
                    <>
                      <p className="text-sm text-yellow-300 mt-1">
                        Groq provides fast cloud-based AI inference. Make sure you have a valid API key.
                      </p>
                      <p className="text-sm text-yellow-300 mt-1">
                        Available models: qwen/qwen3-32b, llama-3.1-8b-instant, mixtral-8x7b-32768
                      </p>
                      <p className="text-sm text-yellow-300 mt-1">
                        <strong>Reasoning parameters:</strong> Only qwen/qwen3-32b supports reasoning_effort. All models support reasoning_format.
                      </p>
                    </>
                  )}
                  {settings.ai.provider === 'openai' && (
                    <p className="text-sm text-yellow-300 mt-1">
                      OpenAI integration is not fully implemented yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Database Configuration */}
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center space-x-3 mb-6">
              <Database className={`w-6 h-6 ${themeClasses.textMuted}`} />
              <h2 className={`text-xl font-semibold ${themeClasses.heading}`}>Database Configuration</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.label}`}>
                  MongoDB URL
                </label>
                <input
                  type="text"
                  value={settings.database.mongoUrl}
                  onChange={(e) => updateSettings('database', 'mongoUrl', e.target.value)}
                  className={`w-full ${themeClasses.input}`}
                  placeholder="mongodb://localhost:27017/webc_v5"
                />
              </div>
            </div>
          </div>

          {/* System Configuration */}
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center space-x-3 mb-6">
              <Settings className={`w-6 h-6 ${themeClasses.textMuted}`} />
              <h2 className={`text-xl font-semibold ${themeClasses.heading}`}>System Configuration</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.label}`}>
                  Base Domain
                </label>
                <input
                  type="text"
                  value={settings.system.baseDomain}
                  onChange={(e) => updateSettings('system', 'baseDomain', e.target.value)}
                  className={`w-full ${themeClasses.input}`}
                  placeholder="localhost:3000"
                />
                <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                  The base domain used for publishing sites. Change from localhost:3000 to your production domain.
                </p>
              </div>
            </div>
          </div>

          {/* Integrations */}
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center space-x-3 mb-6">
              <Globe className="w-6 h-6 text-green-400" />
              <h2 className={`text-xl font-semibold ${themeClasses.heading}`}>External Integrations</h2>
            </div>

            <div className="space-y-6">
              {/* Image Provider Selection */}
              <div className={`border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center space-x-3 mb-4">
                  <Image className={`w-5 h-5 ${themeClasses.textMuted}`} />
                  <h3 className={`font-medium ${themeClasses.heading}`}>Image Provider</h3>
                </div>
                
                {/* Provider Selection */}
                <div className="mb-6">
                  <label className={`block text-sm font-medium ${themeClasses.label}`}>
                    Active Provider
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="imageProvider"
                        value="unsplash"
                        checked={settings.integrations.imageProvider === 'unsplash'}
                        onChange={(e) => updateIntegrationSettings('imageProvider', '', e.target.value)}
                        className="mr-2 text-green-400 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium">Unsplash</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="imageProvider"
                        value="pexels"
                        checked={settings.integrations.imageProvider === 'pexels'}
                        onChange={(e) => updateIntegrationSettings('imageProvider', '', e.target.value)}
                        className={`mr-2 ${themeClasses.input}`}
                      />
                      <span className="text-sm font-medium">Pexels</span>
                    </label>
                  </div>
                </div>

                {/* Unsplash Configuration */}
                {settings.integrations.imageProvider === 'unsplash' && (
                  <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-green-200">Unsplash Configuration</h4>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => testUnsplashConnection()}
                          disabled={testingConnection.unsplash || !settings.integrations.unsplash.accessKey}
                          className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          {testingConnection.unsplash ? 'Testing...' : 'Test'}
                        </button>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.integrations.unsplash.enabled}
                            onChange={(e) => updateIntegrationSettings('unsplash', 'enabled', e.target.checked)}
                            className={`rounded ${themeClasses.input}`}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={settings.integrations.unsplash.accessKey}
                        onChange={(e) => updateIntegrationSettings('unsplash', 'accessKey', e.target.value)}
                        className={`w-full ${themeClasses.input}`}
                        placeholder="Unsplash Access Key"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium ${themeClasses.label}`}>
                            Image Quality
                          </label>
                          <select
                            value={settings.integrations.unsplash.quality || 'regular'}
                            onChange={(e) => updateIntegrationSettings('unsplash', 'quality', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="thumb">Thumbnail (200px)</option>
                            <option value="small">Small (400px)</option>
                            <option value="regular">Regular (1080px)</option>
                            <option value="full">Full (2000px)</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-xs font-medium ${themeClasses.label}`}>
                            Orientation
                          </label>
                          <select
                            value={settings.integrations.unsplash.orientation || 'landscape'}
                            onChange={(e) => updateIntegrationSettings('unsplash', 'orientation', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-600 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                          >
                            <option value="landscape">Landscape</option>
                            <option value="portrait">Portrait</option>
                            <option value="squarish">Square</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-green-300">
                        AI will automatically fetch relevant images from Unsplash during content generation. 
                        <a href="https://unsplash.com/developers" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline ml-1">
                          Get your API key here →
                        </a>
                      </p>
                    </div>
                  </div>
                )}

                {/* Pexels Configuration */}
                {settings.integrations.imageProvider === 'pexels' && (
                  <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-purple-900">Pexels Configuration</h4>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => testPexelsConnection()}
                          disabled={testingConnection.pexels || !settings.integrations.pexels.apiKey}
                          className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                        >
                          {testingConnection.pexels ? 'Testing...' : 'Test'}
                        </button>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.integrations.pexels.enabled}
                            onChange={(e) => updateIntegrationSettings('pexels', 'enabled', e.target.checked)}
                            className={`rounded ${themeClasses.input}`}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={settings.integrations.pexels.apiKey}
                        onChange={(e) => updateIntegrationSettings('pexels', 'apiKey', e.target.value)}
                        className={`w-full ${themeClasses.input}`}
                        placeholder="Pexels API Key"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium ${themeClasses.label}`}>
                            Image Quality
                          </label>
                          <select
                            value={settings.integrations.pexels.quality || 'large'}
                            onChange={(e) => updateIntegrationSettings('pexels', 'quality', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="tiny">Tiny (280px)</option>
                            <option value="small">Small (130px)</option>
                            <option value="medium">Medium (350px)</option>
                            <option value="large">Large (940px)</option>
                            <option value="original">Original (Full Size)</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-xs font-medium ${themeClasses.label}`}>
                            Orientation
                          </label>
                          <select
                            value={settings.integrations.pexels.orientation || 'landscape'}
                            onChange={(e) => updateIntegrationSettings('pexels', 'orientation', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="landscape">Landscape</option>
                            <option value="portrait">Portrait</option>
                            <option value="square">Square</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-purple-700">
                        AI will automatically fetch relevant images from Pexels during content generation. 
                        <a href="https://www.pexels.com/api/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:underline ml-1">
                          Get your API key here →
                        </a>
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* OpenAI */}
              <div className={`border ${themeClasses.border} rounded-lg p-4`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Zap className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-100">OpenAI (Backup AI)</h3>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.integrations.openai.enabled}
                      onChange={(e) => updateIntegrationSettings('openai', 'enabled', e.target.checked)}
                      className="rounded border-gray-600 text-gray-400 focus:ring-blue-500"
                    />
                  </label>
                </div>
                <div className="space-y-3">
                  <input
                    type="password"
                    value={settings.integrations.openai.apiKey}
                    onChange={(e) => updateIntegrationSettings('openai', 'apiKey', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                    placeholder="OpenAI API Key"
                  />
                  <input
                    type="text"
                    value={settings.integrations.openai.model}
                    onChange={(e) => updateIntegrationSettings('openai', 'model', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                    placeholder="gpt-3.5-turbo"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;